// frontend/src/pages/ProviderAreasManagement.tsx
// UPDATED: Mobile-first design with page navigation instead of modals

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Import from new refactored API structure
import { 
  useAreas, 
  useToggleAreaStatus
} from '@/hooks/useAreas';
import { 
  ServiceArea,
  AreasFilters
} from '@/lib/api/areasService';

// Updated components
import AreasListMobile from '@/components/areas/AreasListMobile';
import AreasEmptyMobile from '@/components/areas/AreasEmptyMobile';
import SuccessMessage from '@/components/areas/SuccessMessage';
import { LoadingState, ErrorState, PageHeader, PageFilters, useFilters, StatsGrid, StatItem, FilterPanel } from '@/components/common/ui';
import { MapPin, Globe, TrendingUp, Activity, Target, X } from 'lucide-react';

const ProviderAreasManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // State management
  const [page, setPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Filter management with useFilters hook
  const initialFilters = {
    search: '',
    city: '',
    state: '',
    status: '',
    provider_id: '',
  };
  
  const {
    filters: filterValues,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  } = useFilters(initialFilters);

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Build filters for the API
  const filters: AreasFilters = {
    page,
    limit: 10,
    ...(filterValues.search && { search: filterValues.search }),
    ...(filterValues.city && { city: filterValues.city }),
    ...(filterValues.state && { state: filterValues.state }),
    ...(filterValues.status && { status: filterValues.status }),
    ...(isAdmin && filterValues.provider_id && { provider_id: parseInt(filterValues.provider_id) })
  };

  // API hooks
  const { 
    data: areasData, 
    isLoading, 
    error, 
    refetch 
  } = useAreas(filters);

  const toggleStatusMutation = useToggleAreaStatus();

  // Event handlers
  const handleCreateArea = useCallback(() => {
    if (isAdmin) {
      setSuccessMessage('Admins can view areas but cannot create them. Please contact providers to add areas.');
      return;
    }
    navigate('/provider-areas/add');
  }, [isAdmin, navigate]);

  const handleEditArea = useCallback((area: ServiceArea) => {
    if (isAdmin) {
      navigate(`/provider-areas/view/${area.area_id}`);
    } else {
      navigate(`/provider-areas/edit/${area.area_id}`);
    }
  }, [isAdmin, navigate]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    setPage(1);
  }, [clearFilters]);

  const handleToggleAreaStatus = useCallback(async (area: ServiceArea) => {
    if (isAdmin) return;
    
    try {
      await toggleStatusMutation.mutateAsync(area.area_id);
      setSuccessMessage(`Area ${area.is_active ? 'deactivated' : 'activated'} successfully`);
    } catch (error: any) {
      setSuccessMessage(error.message || 'Failed to update area status');
    }
  }, [isAdmin, toggleStatusMutation]);


  // Data extraction
  const areas = areasData?.areas || [];
  const pagination = areasData?.pagination;
  const stats = areasData?.stats;

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return (
      <ErrorState 
        error={error} 
        onRetry={handleRefresh}
      />
    );
  }

  // Transform stats to match expected interface
  const transformedStats = stats ? {
    totalAreas: stats.total,
    activeAreas: stats.active,
    inactiveAreas: stats.inactive,
    cities: 0,
    states: 0,
    avgRadius: 0
  } : undefined;

  // Transform pagination to match expected interface
  const transformedPagination = pagination ? {
    ...pagination,
    from: ((pagination.current_page - 1) * pagination.per_page) + 1,
    to: Math.min(pagination.current_page * pagination.per_page, pagination.total)
  } : undefined;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Uniform Page Header */}
      <PageHeader
        title={isAdmin ? 'All Service Areas' : 'My Service Areas'}
        subtitle={isAdmin ? 'View and manage all provider service areas' : 'Manage your service coverage areas'}
        icon={MapPin}
        primaryAction={!isAdmin ? {
          label: "Add Area",
          icon: Plus,
          onClick: handleCreateArea,
        } : undefined}
        secondaryActions={[
          {
            label: "Refresh",
            icon: RefreshCw,
            onClick: handleRefresh,
            variant: "outline",
            loading: isLoading,
          },
        ]}
        badge={areasData && {
          text: `${areasData.pagination?.total || areas?.length || 0} areas`,
          variant: "neutral",
        }}
        alert={isAdmin ? {
          type: "info",
          title: "Admin View",
          message: "You're viewing all service areas. Changes can only be made by providers."
        } : undefined}
      />

      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        
        {/* Success Message */}
        {successMessage && (
          <SuccessMessage 
            message={successMessage} 
            onClose={() => setSuccessMessage('')} 
          />
        )}

        {/* Stats */}
        {transformedStats && (
          <div className="mb-6">
            <StatsGrid
              stats={[
                { 
                  label: isAdmin ? 'Total Areas' : 'My Areas', 
                  value: transformedStats.totalAreas || 0,
                  icon: MapPin,
                  color: isAdmin ? 'blue' : 'green',
                  description: isAdmin ? 'System-wide' : undefined
                },
                { 
                  label: 'Active Areas', 
                  value: transformedStats.activeAreas || 0,
                  icon: Activity,
                  color: 'green',
                  progress: transformedStats.totalAreas > 0 
                    ? Math.round((transformedStats.activeAreas / transformedStats.totalAreas) * 100) 
                    : 0,
                  description: transformedStats.totalAreas > 0 
                    ? `${Math.round((transformedStats.activeAreas / transformedStats.totalAreas) * 100)}% active`
                    : undefined
                },
                { 
                  label: 'Inactive Areas', 
                  value: transformedStats.inactiveAreas || 0,
                  icon: X,
                  color: 'red',
                  description: transformedStats.inactiveAreas > 0 ? 'Consider activating' : undefined
                },
                { 
                  label: 'Cities', 
                  value: transformedStats.cities || 0,
                  icon: Globe,
                  color: 'purple',
                  description: isAdmin ? 'Cities with service' : 'Your coverage'
                },
                { 
                  label: 'States', 
                  value: transformedStats.states || 0,
                  icon: TrendingUp,
                  color: 'orange',
                  description: isAdmin ? 'States covered' : 'Your presence'
                },
                { 
                  label: 'Avg Radius', 
                  value: `${transformedStats.avgRadius || 0}km`,
                  icon: Target,
                  color: 'indigo',
                  description: transformedStats.avgRadius > 50 ? 'Wide coverage' : 
                               transformedStats.avgRadius > 20 ? 'Good coverage' : 'Local coverage'
                },
              ]}
              layout="detailed"
              showProgress={true}
              columns={{ mobile: 2, tablet: 3, desktop: 6 }}
            />
          </div>
        )}

        {/* Enhanced Search & Filters */}
        <div className="mb-6">
          <FilterPanel
            fields={[
              {
                key: 'search',
                label: 'Search Areas',
                type: 'search',
                placeholder: 'Search by area name, zip code, or address...',
                width: 'full',
              },
              {
                key: 'city',
                label: 'City',
                type: 'search',
                placeholder: 'Search by city...',
                width: 'md',
              },
              {
                key: 'state',
                label: 'State',
                type: 'search',
                placeholder: 'Search by state...',
                width: 'md',
              },
              {
                key: 'status',
                label: 'Status',
                type: 'select',
                options: [
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' },
                ],
                width: 'md',
              },
              ...(isAdmin ? [{
                key: 'provider_id',
                label: 'Provider',
                type: 'search' as const,
                placeholder: 'Search by provider ID...',
                width: 'md' as const,
              }] : [])
            ]}
            values={filterValues}
            onChange={updateFilter}
            onClear={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            variant="default"
            title={isAdmin ? "Area Filters (Admin)" : "My Area Filters"}
            defaultExpanded={hasActiveFilters}
          />
        </div>

        {/* Areas List or Empty State */}
        <div className="bg-white rounded-lg shadow-sm">
          {areas.length === 0 ? (
            <div className="p-6">
              <AreasEmptyMobile 
                hasFilters={hasActiveFilters}
                onCreateArea={handleCreateArea}
                onClearFilters={handleClearFilters}
                isAdmin={isAdmin}
              />
            </div>
          ) : (
            <AreasListMobile
              areas={areas}
              pagination={transformedPagination}
              currentPage={page}
              onPageChange={setPage}
              onEditArea={handleEditArea}
              onToggleStatus={handleToggleAreaStatus}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </div>

      {/* Floating Action Button (Mobile) */}
      {!isAdmin && (
        <div className="fixed bottom-6 right-6 z-20">
          <button
            onClick={handleCreateArea}
            className="w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProviderAreasManagement;